import { zodResolver } from '@hookform/resolvers/zod'
import { toFullName } from '@repo/toolkit/to-full-name'
import Checkbox from '@repo/ui/checkbox'
import { BellIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z, { ZodType } from 'zod'

import { ApiUrls } from '../../../api/apis.config'
import { getUsers } from '../../../api/user/get-users'
import { sendEmail } from '../../../lib/api/send-email'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { SendEmailInput } from '../../../lib/types/models/mail/create-mail-attachment'
import {
  getSubmitAction,
  modalButtonConfig
} from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { RICH_TEXT_SCHEMA } from '../../../lib/utils/validations/rich-text.validation'
import { createStringArraySchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'
import ValidationButtons from '../../elements/validation-buttons'

type EmailFormInput = {
  emails: string[]
  body: string
  subject?: string
}

type Props<T extends object = object> = {
  label: string
  isOpen: boolean
  emailApiEndpoint: ApiUrls
  customCheckbox?: {
    label: string
    defaultChecked?: boolean
  }
  // lets the caller submit without users or body (e.g. when only the checkbox action is wanted)
  optionalContent?: boolean
  beforeSubmit?: (input: EmailFormInput, checked: boolean) => SendEmailInput<T>
  closeModal: () => void
  onSuccess?: () => void
}

const formSchema: ZodType<EmailFormInput> = z.object({
  emails: createStringArraySchema('emails'),
  body: RICH_TEXT_SCHEMA('fr')
})

const optionalFormSchema: ZodType<EmailFormInput> = z.object({
  emails: z.array(z.string()),
  body: z.string()
})

const hasHtmlContent = (html?: string) =>
  !!html?.replace(/<[^>]*>?/gm, '').replace(/&nbsp;|\s/g, '')

export function EmailModal<T extends object = object>({
  label,
  isOpen,
  customCheckbox,
  emailApiEndpoint,
  optionalContent,
  beforeSubmit,
  closeModal,
  onSuccess
}: Props<T>) {
  const [checked, setChecked] = useState(customCheckbox?.defaultChecked || false)
  const form = useForm<EmailFormInput>({
    resolver: zodResolver(optionalContent ? optionalFormSchema : formSchema),
    defaultValues: optionalContent ? { emails: [], body: '' } : {},
    mode: FORM_MODE
  })

  const { mutate: sendEmailMutation } = useMutation({
    mutationFn: (input: SendEmailInput<T>) => sendEmail(emailApiEndpoint, input),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModal()
      if (onSuccess) onSuccess()
    }
  })

  const emails = form.watch('emails')
  const hasBody = hasHtmlContent(form.watch('body'))
  // at least one recipient (LEC users or the checkbox action), and selected users need a message
  const isOptionalContentInvalid = !!optionalContent && (emails?.length ? !hasBody : !checked)

  const validationButtonProps = modalButtonConfig({
    disabled: isOptionalContentInvalid,
    confirmText: 'Envoyer',
    onCancel: () => closeModal(),
    onConfirm: (values: EmailFormInput) => {
      // we are not waiting for the response to close the modal
      // since we are sending the email to the admins and we don't
      // want to block the user from doing other actions
      const inputs = beforeSubmit ? beforeSubmit(values, checked) : (values as SendEmailInput<T>)
      sendEmailMutation(inputs)
      form.reset()
      setChecked(customCheckbox?.defaultChecked || false)
      closeModal()
    }
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins', 'master-lec'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } }),
    enabled: isOpen
  })

  const usersOptions = users?.data?.map((u) => ({
    label: toFullName(u.firstName, u.lastName),
    value: u.email
  }))

  return (
    <Modal
      hideActions
      size="medium"
      icon={BellIcon}
      title={'Envoyer une notification'}
      isOpen={isOpen}
      onCancel={closeModal}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps))}>
          <div className={'flex gap-4 flex-col modal-body'}>
            <Typography variant="tag">{label}</Typography>

            <FormField
              control={form.control}
              name={'emails'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      required={!optionalContent}
                      variant="primary"
                      searchable
                      {...field}
                      type={'text'}
                      label={'Prévenir des collaborateurs LEC'}
                      options={usersOptions}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'body'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledTextEditor
                      required={!optionalContent}
                      {...field}
                      placeholder={'Écrivez votre message ici'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {customCheckbox && (
              <Checkbox
                checked={checked}
                onChange={setChecked}
                label={customCheckbox.label}
                wrapperClassName="border rounded p-2 border-grey-light"
              />
            )}
          </div>

          <ValidationButtons buttonsConfig={validationButtonProps} hasBorderTop />
        </form>
      </Form>
    </Modal>
  )
}
